import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ExternalLink, Plus, X, Check, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { SiteNav } from '@/components/SiteNav';

const Editable = ({ value, onChange, placeholder, className, multiline = false, testId, editable }) => {
  if (!editable) {
    return <span className={className} data-testid={testId}>{value || placeholder}</span>;
  }
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <Tag
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`${className} bg-transparent border-0 border-b border-transparent hover:border-[#E5E5E5] focus:border-black focus:outline-none focus:bg-white/60 px-1 -mx-1 transition-colors w-full resize-none`}
      data-testid={testId}
      {...(multiline ? { rows: 1 } : {})}
    />
  );
};

const WorkHistory = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [user, setUser] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
    const token = localStorage.getItem('session_token');
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUser(r.data))
        .catch(() => {
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
        });
    }
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* ignore */ }
    }
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Debounced save (admin inline edits)
  const queueSave = (next) => {
    setSettings(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      try {
        await axios.put(`${API}/settings`, next, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Saved', { duration: 1000 });
      } catch (e) {
        toast.error('Save failed');
      }
    }, 700);
  };

  if (!settings) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="animate-pulse text-xs tracking-[0.3em] uppercase text-[#525252]">Loading</div></div>;
  }

  const L = settings.labels || {};
  const categories = settings.selected_works_categories || [];
  const timeline = settings.work_history || [];
  const isAdmin = !!user;

  // Flatten items across years and keep indices so admin edits target the right entry
  const allItems = [];
  timeline.forEach((period, yi) => {
    (period.items || []).forEach((item, ii) => {
      allItems.push({ ...item, _year: period.year, _yi: yi, _ii: ii });
    });
  });

  const itemsByCategory = {};
  categories.forEach(cat => { itemsByCategory[cat] = []; });
  const uncategorized = [];
  allItems.forEach(item => {
    if (item.category && itemsByCategory[item.category] !== undefined) {
      itemsByCategory[item.category].push(item);
    } else {
      uncategorized.push(item);
    }
  });
  // Sort by year, most recent first. Pulls a 4-digit year from any of the item's text fields
  // (venue, description, or title), falling back to the period.year (_year) field.
  const yearOf = (it) => {
    const haystack = `${it.venue || ''} ${it.description || ''} ${it.title || ''}`;
    const m = haystack.match(/(19|20)\d{2}/);
    if (m) return parseInt(m[0], 10);
    const fromYear = parseInt(it._year, 10);
    return Number.isFinite(fromYear) ? fromYear : -Infinity;
  };
  const sortByYearDesc = (a, b) => yearOf(b) - yearOf(a);
  Object.keys(itemsByCategory).forEach(cat => {
    itemsByCategory[cat].sort(sortByYearDesc);
  });
  uncategorized.sort(sortByYearDesc);
  allItems.sort(sortByYearDesc);

  // --- Admin mutation helpers ---
  const updateItemField = (yi, ii, field, value) => {
    const wh = settings.work_history.map((p, i) => {
      if (i !== yi) return p;
      const items = p.items.map((it, j) => j === ii ? { ...it, [field]: value } : it);
      return { ...p, items };
    });
    queueSave({ ...settings, work_history: wh });
  };

  const removeItem = (yi, ii) => {
    const wh = settings.work_history.map((p, i) => {
      if (i !== yi) return p;
      return { ...p, items: p.items.filter((_, j) => j !== ii) };
    });
    queueSave({ ...settings, work_history: wh });
  };

  const addItemToCategory = (cat) => {
    // Add to the first year group, or create one
    const wh = [...(settings.work_history || [])];
    const newEntry = { title: 'New entry', venue: '', url: '', category: cat || '' };
    if (wh.length === 0) {
      wh.push({ year: new Date().getFullYear().toString(), items: [newEntry] });
    } else {
      wh[0] = { ...wh[0], items: [newEntry, ...wh[0].items] };
    }
    queueSave({ ...settings, work_history: wh });
  };

  const updateCategoryName = (index, value) => {
    const cats = [...categories];
    const old = cats[index];
    cats[index] = value;
    const wh = settings.work_history.map(p => ({
      ...p,
      items: (p.items || []).map(it => it.category === old ? { ...it, category: value } : it)
    }));
    queueSave({ ...settings, selected_works_categories: cats, work_history: wh });
  };

  const removeCategory = (index) => {
    if (!window.confirm('Remove this column? Entries inside become uncategorized.')) return;
    const removed = categories[index];
    const cats = categories.filter((_, i) => i !== index);
    const wh = settings.work_history.map(p => ({
      ...p,
      items: (p.items || []).map(it => it.category === removed ? { ...it, category: '' } : it)
    }));
    queueSave({ ...settings, selected_works_categories: cats, work_history: wh });
  };

  const addCategory = () => {
    queueSave({ ...settings, selected_works_categories: [...categories, 'NEW COLUMN'] });
  };

  // Inline editable text (component declared at module scope)
  const editable = isAdmin;

  const renderItem = (item) => {
    const { _yi: yi, _ii: ii } = item;
    if (isAdmin) {
      return (
        <div
          key={`${yi}-${ii}`}
          className="space-y-1 group relative -mx-2 px-2 py-1 rounded hover:bg-white transition-colors"
          data-testid={`work-item-${yi}-${ii}`}
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 space-y-1">
              <Editable
                value={item.title}
                onChange={(v) => updateItemField(yi, ii, 'title', v)}
                placeholder="Title"
                className="text-[10px] sm:text-[11px] tracking-[0.08em] uppercase text-[#262626]"
                testId={`item-title-${yi}-${ii}`}
                editable={editable}
              />
              <Editable
                value={item.venue}
                onChange={(v) => updateItemField(yi, ii, 'venue', v)}
                placeholder="Location"
                className="text-[10px] sm:text-[11px] tracking-[0.08em] uppercase text-[#525252] block"
                testId={`item-venue-${yi}-${ii}`}
                editable={editable}
              />
              <Editable
                value={item.description}
                onChange={(v) => updateItemField(yi, ii, 'description', v)}
                placeholder="Explanation"
                className="text-[10px] sm:text-[11px] tracking-[0.08em] uppercase text-[#A3A3A3] block italic"
                testId={`item-description-${yi}-${ii}`}
                editable={editable}
              />
              {/* Year — shown clean by default, becomes editable on hover */}
              {/* Year line hidden — year is part of venue text now */}
              {/* Open-link indicator when not hovering */}
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden group-hover:hidden text-xs text-[#A3A3A3] items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-3 h-3" strokeWidth={2} />
                </a>
              )}
            </div>
            {/* Delete button — only on hover */}
            <button
              onClick={() => removeItem(yi, ii)}
              className="opacity-0 group-hover:opacity-100 text-[#A3A3A3] hover:text-black transition-opacity shrink-0 mt-1"
              data-testid={`remove-item-${yi}-${ii}`}
              title="Remove"
            >
              <X className="w-3 h-3" strokeWidth={1.5} />
            </button>
          </div>

          {/* Hover-only edit chrome: link + year inputs */}
          <div className="hidden group-hover:block space-y-1 pt-1">
            <div className="flex items-center gap-2">
              <Link2 className="w-3.5 h-3.5 text-[#A3A3A3] shrink-0" strokeWidth={1.75} />
              <input
                type="url"
                value={item.url || ''}
                onChange={(e) => updateItemField(yi, ii, 'url', e.target.value)}
                placeholder="Paste article / link URL here"
                className="text-xs text-[#525252] flex-1 bg-transparent border-0 border-b border-[#E5E5E5] hover:border-[#A3A3A3] focus:border-black focus:outline-none px-1 py-0.5 placeholder:text-[#A3A3A3]"
                data-testid={`item-url-${yi}-${ii}`}
              />
              {item.url && (
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[#525252] hover:text-black" title="Open link">
                  <ExternalLink className="w-3 h-3" strokeWidth={2} />
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }
    // Public view — Nadia-style: single uppercase tracked line, "TITLE - LOCATION - EXPLANATION" combined
    const inline = [item.title, item.venue, item.description].filter(Boolean).join(' — ');
    return item.url ? (
      <a
        key={`${yi}-${ii}`}
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block text-[10px] sm:text-[11px] tracking-[0.08em] uppercase text-[#262626] hover:text-black hover:underline underline-offset-4 decoration-1 leading-relaxed transition-colors"
        data-testid={`work-item-${yi}-${ii}`}
      >
        {inline}
      </a>
    ) : (
      <p
        key={`${yi}-${ii}`}
        className="text-[10px] sm:text-[11px] tracking-[0.08em] uppercase text-[#262626] leading-relaxed"
        data-testid={`work-item-${yi}-${ii}`}
      >
        {inline}
      </p>
    );
  };

  // Helpers needed by SiteNav so logo and nav labels are inline-editable too
  const updateField = (key, value) => queueSave({ ...settings, [key]: value });
  const updateLabel = (key, value) => queueSave({ ...settings, labels: { ...(settings.labels || {}), [key]: value } });

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="work-history-page">
      <SiteNav settings={settings} isAdmin={isAdmin} setUser={setUser} updateField={updateField} updateLabel={updateLabel} active="work" />

      <div className="px-4 sm:px-6 md:px-12 lg:px-24 py-12 sm:py-20">
        <div className="max-w-6xl mx-auto">
          {isAdmin && (
            <div className="mb-8 flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#525252]">
              <Check className="w-3 h-3" strokeWidth={2} />
              <span>Inline editing on — changes save automatically</span>
            </div>
          )}

          {categories.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-6">
              {categories.map((cat, ci) => (
                <div key={ci} className="space-y-4" data-testid={`column-${ci}`}>
                  <div className="pb-3 flex items-start gap-2 group">
                    {isAdmin ? (
                      <>
                        <input
                          value={cat}
                          onChange={(e) => updateCategoryName(ci, e.target.value)}
                          className="text-[11px] sm:text-xs tracking-[0.1em] uppercase font-bold bg-transparent border-0 focus:outline-none focus:bg-white/60 flex-1 w-full"
                          data-testid={`column-name-${ci}`}
                        />
                        <button onClick={() => removeCategory(ci)} className="opacity-0 group-hover:opacity-100 text-[#A3A3A3] hover:text-black" title="Remove column" data-testid={`remove-column-${ci}`}>
                          <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </>
                    ) : (
                      <h3 className="text-[11px] sm:text-xs tracking-[0.1em] uppercase font-bold">{cat}</h3>
                    )}
                  </div>
                  <div className="space-y-2">
                    {itemsByCategory[cat]?.length > 0 ? (
                      itemsByCategory[cat].map(item => renderItem(item))
                    ) : (
                      <p className="text-[10px] text-[#A3A3A3] tracking-[0.08em] uppercase">—</p>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => addItemToCategory(cat)}
                        className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#A3A3A3] hover:text-black flex items-center gap-1.5 pt-2"
                        data-testid={`add-to-${ci}`}
                      >
                        <Plus className="w-3 h-3" strokeWidth={2} /> Add entry
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {isAdmin && (
                <button
                  onClick={addCategory}
                  className="border border-dashed border-[#E5E5E5] hover:border-black p-6 text-[10px] tracking-[0.2em] uppercase font-bold text-[#A3A3A3] hover:text-black flex items-center justify-center gap-2 min-h-[80px]"
                  data-testid="add-column"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} /> Add Column
                </button>
              )}
              {uncategorized.length > 0 && (
                <div className="space-y-3 sm:col-span-2 lg:col-span-5 border-t border-[#E5E5E5] pt-8 mt-4">
                  <h3 className="text-[11px] tracking-[0.1em] uppercase font-bold text-[#525252]">
                    Uncategorized {isAdmin && '— pick a column on each entry below'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    {uncategorized.map(item => renderItem(item))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {allItems.length === 0 && (
                <div className="text-center py-16 bg-white border border-[#E5E5E5]">
                  <p className="text-[#525252] font-serif italic">{L.work_empty || 'No entries yet.'}</p>
                </div>
              )}
              {allItems.map(item => renderItem(item))}
              {isAdmin && (
                <button
                  onClick={addCategory}
                  className="border border-dashed border-[#E5E5E5] hover:border-black p-6 text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3] hover:text-black flex items-center justify-center gap-2 w-full"
                  data-testid="add-first-column"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} /> Add first column
                </button>
              )}
            </div>
          )}

          <div className="mt-16 border-t border-[#E5E5E5] pt-8">
            <p className="text-sm text-[#525252] font-serif italic">
              {L.work_footer || 'For a complete CV or press kit, please contact:'} {settings.contact_email || 'hello@therabbithole.com'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkHistory;
